import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useImpler} from "@impler/react";
import {showToastSuccess} from "ubs-framework/v2/common/function";
import useSocket from "./useSocket";
import {sendData} from "../../../store/actions";
import {getImplerProjectId, getImplerTemplateId, getImplerTokenId} from "../../../common/impler";
import {IMPORT_REPORT_TYPE, JOB_IMPORT_OPTIONS} from "../../../common/constant";

// key = Impler column (backend validation record key), out = webhook key — must match routes/bulkImports/importJob.js
const JOB_IMPORT_COLUMNS = [
    {key: 'Job ID', out: 'jobId', isUnique: true},
    {key: 'Job Name', out: 'name', isRequired: true},
    {key: 'Job Title', out: 'jobTitle', isRequired: true, options: 'jobTitle'},
    {key: 'Job Post Type', out: 'jobPostType', isRequired: true, selectValues: JOB_IMPORT_OPTIONS.JOB_POST_TYPE},
    {key: 'Job Type', out: 'jobType', isRequired: true, options: 'jobType'},
    {key: 'Department', out: 'department', isRequired: true, options: 'department'},
    {key: 'Location', out: 'location', isRequired: true, options: 'location', allowMultiSelect: true},
    {key: 'Total Openings', out: 'totalOpenings', isRequired: true, type: 'Number'},
    {key: 'Hiring Manager (Employee Code)', out: 'hiringManager'},
    {key: 'Hiring Team (Employee Code)', out: 'hiringTeam'},
    {key: 'Grade', out: 'grade', options: 'gradeList'},
    {key: 'Bands', out: 'bands', options: 'bandList'},
    {key: 'Opening Date', out: 'openingDate', isRequired: true, type: 'Date'},
    {key: 'Expiry Date', out: 'expireDate', isRequired: true, type: 'Date'},
    {key: 'Notify Before (in Days)', out: 'notifyExpireDay', type: 'Number'},
    {key: 'Salary Type', out: 'salaryType', isRequired: true, selectValues: JOB_IMPORT_OPTIONS.SALARY_TYPE},
    {key: 'Salary Mode', out: 'salaryMode', isRequired: true, selectValues: JOB_IMPORT_OPTIONS.SALARY_MODE},
    {key: 'Salary / Salary Range', out: 'salaryRange'},
    {key: 'Note', out: 'negotiableNote'},
    {key: 'Experience (In Year)', out: 'experience', isRequired: true},
    {key: 'Status', out: 'status', isRequired: true, options: 'status'},
    {key: 'Priority', out: 'priority', isRequired: true, options: 'priority'},
    {key: 'Must Have Skill', out: 'mustHaveSkill', isRequired: true, options: 'skill', allowMultiSelect: true},
    {key: 'Good to Have Skill', out: 'goodToHaveSkill', isRequired: true, options: 'skill', allowMultiSelect: true},
    {key: 'Gender Preference', out: 'genderPreference', selectValues: JOB_IMPORT_OPTIONS.GENDER_PREFERENCE},
    {key: 'Tags', out: 'tags', options: 'tags', allowMultiSelect: true},
    {key: 'Screening Question Template', out: 'screeningQuestionId', options: 'screeningTemplate'},
    // blank -> no custom fields / the default pipeline, same as the job form
    {key: 'Custom Fields Template', out: 'customFieldTemplate', options: 'customFieldTemplate'},
    {key: 'Recruitment Stages Template', out: 'pipelineTemplate', isRequired: true, options: 'pipelineTemplate'},
    {key: 'Interview Form Template', out: 'interviewFormTemplate', options: 'interviewFormTemplate'},
    {key: 'Job Description', out: 'description', isRequired: true},
    {key: 'Company Perks', out: 'companyPerks', isRequired: true},
    {key: 'Save As', out: 'saveAs', isRequired: true, selectValues: JOB_IMPORT_OPTIONS.SAVE_AS},
];

/** Opens the Impler job importer; returns the function a button / menu item calls. */
const useImportJobs = () => {
    const dispatch = useDispatch();

    const _ORG_ID = useSelector(e => e.common.orgId);
    const _ID = useSelector(e => e?.common?._id);

    const [importData, setImportData] = useState();
    // the toolbar and the jobs list both mount this hook; only the one that asked opens the widget
    const isRequestedRef = useRef(false);

    const {showWidget} = useImpler({
        templateId: getImplerTemplateId('importJob'),
        projectId: getImplerProjectId(),
        accessToken: getImplerTokenId(),
        authHeaderValue: getImplerTokenId(),
        extra: {orgId: _ORG_ID, userId: _ID, type: IMPORT_REPORT_TYPE.IMPORT_JOB, importDate: new Date()},
        onWidgetClose: () => {
            setImportData(null);
            // a fresh filter object re-runs the job list fetch, so imported jobs show up
            dispatch({type: 'FILTER_JOBS', payload: {}});
        },
        onUploadComplete: () => {
            showToastSuccess('Jobs imported successfully. Check Reports > Import Summary Report for the status.');
        },
    });

    const {off, refresh} = useSocket(res => {
        if (res.event !== 'master|bulkupload|excel|generator' || !isRequestedRef.current) return;
        isRequestedRef.current = false;
        if (!res.error) setImportData(res.data);
    });

    useEffect(() => {
        refresh();
        return () => off();
    }, []);

    useEffect(() => {
        if (!importData) return;
        const schema = [];
        const dataOutput = {};
        JOB_IMPORT_COLUMNS.forEach(column => {
            // in auto Job ID mode a blank Job ID is generated by the backend
            const isRequired = column.out === 'jobId' ? !importData?.isAutoJobId : !!column.isRequired;
            // masters can hold the same name twice; the backend resolves by name, so one entry is enough
            const selectValues = column.selectValues || (column.options ? [...new Set(importData?.[column.options]?.map(e => e?.name?.trim())?.filter(Boolean))] : null);
            schema.push({
                name: isRequired ? `${column.key}*` : column.key,
                key: column.key,
                type: selectValues ? 'Select' : (column.type || 'String'),
                isRequired,
                ...(column.isUnique && {isUnique: true}),
                ...(selectValues && {selectValues}),
                // Impler adds "#MULTI" to the sample header; the backend validator trims "a, b" into "a,b" for it
                ...(column.allowMultiSelect && {allowMultiSelect: true, delimiter: ','}),
                ...(column.type === 'Date' && {dateFormats: JOB_IMPORT_OPTIONS.DATE_FORMATS}),
            });
            dataOutput[column.out] = `{{${column.key}}}`;
        });
        showWidget({
            schema,
            output: {
                "%data%": dataOutput,
                "page": "{{page}}",
                "chunkSize": "{{chunkSize}}",
                "template": "{{template}}",
                "uploadId": "{{uploadId}}",
                "fileName": "{{fileName}}",
                "extra": "{{extra}}",
                "totalPage": "{{totalPages}}",
                "totalRecords": "{{totalRecords}}",
            },
        });
    }, [importData]);

    return () => {
        isRequestedRef.current = true;
        sendData('master|bulkupload|excel|generator', {type: IMPORT_REPORT_TYPE.IMPORT_JOB});
    };
};

export default useImportJobs;
